/**
 * Patient360 — Adverse Event / Product Complaint Tab
 * (Enterprise Pharmacovigilance Operations Workspace)
 *
 * Layout:
 * - Filter chips + Quick Report templates + Search
 * - 2-column: Left (severity-coded timeline) + Right (safety insights / alerts / follow-ups)
 * - Right drawer for AE reporting (no redirect / no modal)
 * - Smart alerts for SLA breaches / risk signals
 *
 * Lifecycle: DRAFT → SUBMITTED → PV_REVIEW → CLOSED
 *
 * §21 (no hardcoded FormId/Version)
 * §23 (compact UI), §24 (icon size 12–14px, lucide only)
 * §26 (Patient360 normalization)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Hash,
  MoreVertical,
  Paperclip,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  Shield,
  ShieldCheck,
  Trash2,
  Upload,
  X,
  Zap,
} from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { colorSystem } from '@/design-system/colorSystem';
import { AppButtonLoader, AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { StatusBadge } from '@/modules/shared/components/ui/StatusBadge';
import { Button } from '@/modules/shared/components/ui/button';
import { Switch } from '@/modules/shared/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/shared/components/ui/dropdown-menu';
import {
  AppModal,
  AppModalHeader,
  AppModalBody,
  AppModalFooter,
  AppModalTitle,
  AppModalDescription,
} from '@/modules/shared/components/ui/AppModal';
import { useToast } from '@/modules/shared/components/ui/toast';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { programRoutes } from '@/modules/shared/constants/routes';
import { communicationService } from '@/modules/shared/services/communicationService';
import type { Communication } from '@/modules/shared/types/communication';

import { formatDisplayDate } from '../../../utils/formatUtils';
import { normalizePhase, isSeriousEvent, computeAeSmartAlerts, type LifecyclePhase } from '../../../utils/adverseEventAlerts';
import type { Patient360AdverseEventDto } from '../../../types/patient360';
import { adverseEventService } from '../../../services/adverseEventService';
import type { AeDocumentUploadResult } from '../../../services/adverseEventService';
import { aeRegulatoryExportService } from '../../../services/aeRegulatoryExportService';
import { aeEscalationService } from '../../../services/aeEscalationService';
import { notificationConfigurationService } from '../../../services/notificationConfigurationService';
import type {
  AdverseEventDetailDto,
  AdverseEventFollowUpRequest,
  AdverseEventManualEscalationRequest,
  AdverseEventTimelineEntryDto,
} from '../../../types/adverseEvent';
import type { AeEscalationHistoryDto } from '../../../types/pharmacovigilance';

// ─── Props ───────────────────────────────────────────────────

interface AdverseEventsTabProps {
  programId: string;
  patientId: string;
  adverseEvents: Patient360AdverseEventDto[];
  currentCycleId?: string | null;
  onRefresh?: () => void;
  patientName?: string;
  patientCode?: string;
}

// ─── Lifecycle helpers ───────────────────────────────────────
// normalizePhase / isSeriousEvent moved to utils/adverseEventAlerts.ts (shared
// with the Patient360 Needs Attention summary — single source of truth).

type StatusFilter = 'all' | 'open' | 'closed' | 'serious' | 'escalated';

function phaseBadge(phase: LifecyclePhase) {
  switch (phase) {
    case 'DRAFT': return { variant: 'muted' as const, label: 'Draft' };
    case 'SUBMITTED': return { variant: 'info' as const, label: 'Submitted' };
    case 'PV_REVIEW': return { variant: 'warning' as const, label: 'PV review' };
    case 'CLOSED': return { variant: 'success' as const, label: 'Closed' };
  }
}

function severityInfo(severity: string | null | undefined): {
  variant: 'error' | 'warning' | 'success' | 'muted';
  label: string;
  color: string;
  bgColor: string;
  dotColor: string;
} {
  const s = (severity || '').toUpperCase();
  if (s === 'CRITICAL' || s === 'LIFE_THREATENING')
    return { variant: 'error', label: 'Critical', color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-950/30', dotColor: 'bg-red-600' };
  if (s === 'SEVERE' || s === 'HIGH')
    return { variant: 'error', label: 'Severe', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-950/20', dotColor: 'bg-red-500' };
  if (s === 'MODERATE' || s === 'MEDIUM')
    return { variant: 'warning', label: 'Moderate', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-950/20', dotColor: 'bg-amber-500' };
  if (s === 'MILD' || s === 'LOW')
    return { variant: 'success', label: 'Mild', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-950/20', dotColor: 'bg-emerald-500' };
  return { variant: 'muted', label: severity || '—', color: 'text-muted-foreground', bgColor: 'bg-muted/30', dotColor: 'bg-muted-foreground' };
}

function isPvRole(roleName?: string): boolean {
  if (!roleName) return false;
  const r = roleName.toUpperCase();
  return r === 'PV' || r.includes('PHARMACOVIGILANCE') || r === 'ROCHE ADMIN' || r === 'ROCHE_ADMIN';
}

function groupByDate(events: Patient360AdverseEventDto[]): Map<string, Patient360AdverseEventDto[]> {
  const groups = new Map<string, Patient360AdverseEventDto[]>();
  for (const e of events) {
    const date = formatDisplayDate(e.eventDate || e.createdOnUtc);
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date)!.push(e);
  }
  return groups;
}

// ─── Form state ──────────────────────────────────────────────

interface AeAttachment {
  s3Key: string;
  signedUrl: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

interface AeFormState {
  reportType: 'AE' | 'PC' | 'BOTH';
  severity: string;
  eventDate: string;
  description: string;
  suspectedProduct: string;
  eventType: string;
  causality: 'YES' | 'NO' | 'UNKNOWN' | '';
  seriousEvent: boolean;
  outcome: string;
  escalateToPv: boolean;
  attachments: AeAttachment[];
}

const EMPTY_FORM: AeFormState = {
  reportType: 'AE',
  severity: 'MODERATE',
  eventDate: new Date().toISOString().slice(0, 10),
  description: '',
  suspectedProduct: '',
  eventType: '',
  causality: '',
  seriousEvent: false,
  outcome: '',
  escalateToPv: false,
  attachments: [],
};

function buildFormPayload(form: AeFormState): string {
  return JSON.stringify({
    suspected_product: form.suspectedProduct || null,
    event_type: form.eventType || null,
    causality: form.causality || null,
    report_type: form.reportType,
    serious_event: form.seriousEvent,
    outcome: form.outcome || null,
    escalate_to_pv: form.escalateToPv,
    attachments: form.attachments.map(a => ({
      s3_key: a.s3Key,
      file_name: a.fileName,
      content_type: a.contentType,
      file_size: a.fileSize,
    })),
  });
}

// ─── Sub-Components ──────────────────────────────────────────

/** Smart Alerts */
function SmartAlerts({ events }: { events: Patient360AdverseEventDto[] }) {
  const alerts = computeAeSmartAlerts(events);

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-1.5">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={cn(
            'flex items-start gap-2 rounded-md border px-3 py-2 text-[11px]',
            a.type === 'error' && 'border-red-200 bg-red-50/50 text-red-700 dark:border-red-900 dark:bg-red-950/20 dark:text-red-400',
            a.type === 'warning' && 'border-amber-200 bg-amber-50/50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-400',
            a.type === 'info' && 'border-blue-200 bg-blue-50/50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/20 dark:text-blue-400',
          )}
        >
          <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
          <span>{a.message}</span>
        </div>
      ))}
    </div>
  );
}

/** Timeline AE Card */
function AeTimelineCard({
  event,
  onView,
  onEdit,
  onAddFollowUp,
  onEscalate,
  onDownloadPdf,
  onNotifyPv,
  onResendEscalationEmail,
  onSendNotification,
  onViewAuditTrail,
}: {
  event: Patient360AdverseEventDto;
  onView: () => void;
  onEdit: () => void;
  onAddFollowUp: () => void;
  onEscalate: () => void;
  onDownloadPdf: () => void;
  onNotifyPv: () => void;
  onResendEscalationEmail: () => void;
  onSendNotification: () => void;
  onViewAuditTrail: () => void;
}) {
  const phase = normalizePhase(event.status);
  const ph = phaseBadge(phase);
  const sev = severityInfo(event.severity);
  const reportCode = event.reportCode || event.adverseEventId.slice(0, 8).toUpperCase();
  const reportType = event.reportType || 'Adverse Event';
  const serious = isSeriousEvent(event.severity);

  return (
    <div
      className={cn(
        'group relative flex gap-3 cursor-pointer',
        serious
          ? 'p360-ae-card border-l-4 border-l-red-600 dark:border-l-red-500'
          : 'rounded-xl border border-border bg-card px-3.5 py-3 transition-all hover:border-primary/30',
      )}
      onClick={onView}
    >
      {/* Severity dot */}
      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', sev.bgColor)}>
        <div className={cn('h-2.5 w-2.5 rounded-full', sev.dotColor)} />
      </div>

      {/* Content — priority order: Type | Severity | Reported | Status | Action */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Identity + Type + Severity + Status */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-semibold text-foreground">{reportCode}</span>
          <span className="text-[11px] text-muted-foreground">{reportType}</span>
          <StatusBadge status={sev.label} variant={sev.variant} />
          <StatusBadge status={ph.label} variant={ph.variant} />
          {event.isSlaBreached && (
            <span className="inline-flex items-center gap-0.5 rounded bg-red-100 dark:bg-red-950/30 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400">
              SLA BREACH
            </span>
          )}
        </div>

        {/* Row 2: Reported date + Description */}
        <div className="mt-1">
          {event.eventDate && (
            <span className="text-[11px] text-muted-foreground">Reported: {formatDisplayDate(event.eventDate)}</span>
          )}
          {event.description && (
            <p className="text-[11px] text-foreground/80 line-clamp-2 mt-0.5">{event.description}</p>
          )}
        </div>

        {/* Row 3: Remaining metadata */}
        <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-1.5 text-[11px] text-muted-foreground">
          {event.currentWorkflowStepName && <span>Step: {event.currentWorkflowStepName}</span>}
          {event.lcnNumber && <span className="font-mono">LCN: {event.lcnNumber}</span>}
        </div>
      </div>

      {/* Kebab */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem className="text-[11px]" onClick={onView}>View Details</DropdownMenuItem>
            {phase === 'DRAFT' && <DropdownMenuItem className="text-[11px]" onClick={onEdit}>Edit Draft</DropdownMenuItem>}
            {phase !== 'CLOSED' && <DropdownMenuItem className="text-[11px]" onClick={onAddFollowUp}>Add Follow-up</DropdownMenuItem>}
            {(phase === 'SUBMITTED' || phase === 'PV_REVIEW') && <DropdownMenuItem className="text-[11px]" onClick={onEscalate}>Escalate</DropdownMenuItem>}
            <DropdownMenuItem className="text-[11px]" onClick={onDownloadPdf}>Download PDF</DropdownMenuItem>
            {phase !== 'CLOSED' && <DropdownMenuItem className="text-[11px]" onClick={onNotifyPv}>Notify PV Team</DropdownMenuItem>}
            <DropdownMenuItem className="text-[11px]" onClick={onResendEscalationEmail}>Resend Escalation Email</DropdownMenuItem>
            <DropdownMenuItem className="text-[11px]" onClick={onSendNotification}>Send Notification</DropdownMenuItem>
            <DropdownMenuItem className="text-[11px]" onClick={onViewAuditTrail}>View Audit Trail</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Right Panel: Safety Insights */
function SafetyInsightsPanel({ events }: { events: Patient360AdverseEventDto[] }) {
  const total = events.length;
  const closed = events.filter(e => normalizePhase(e.status) === 'CLOSED').length;
  const resolutionRate = total > 0 ? Math.round((closed / total) * 100) : 0;

  const pendingEscalations = events.filter(e =>
    isSeriousEvent(e.severity) && normalizePhase(e.status) !== 'CLOSED'
  );

  const recentFollowUps = events
    .filter(e => e.slaDeadlineUtc && normalizePhase(e.status) !== 'CLOSED')
    .sort((a, b) => new Date(a.slaDeadlineUtc!).getTime() - new Date(b.slaDeadlineUtc!).getTime())
    .slice(0, 4);

  // Severity breakdown
  const severityCounts = {
    critical: events.filter(e => ['CRITICAL', 'LIFE_THREATENING'].includes((e.severity || '').toUpperCase())).length,
    severe: events.filter(e => ['SEVERE', 'HIGH'].includes((e.severity || '').toUpperCase())).length,
    moderate: events.filter(e => ['MODERATE', 'MEDIUM'].includes((e.severity || '').toUpperCase())).length,
    mild: events.filter(e => ['MILD', 'LOW'].includes((e.severity || '').toUpperCase())).length,
  };

  return (
    <div className="space-y-3">
      {/* Risk Summary */}
      <div className="p360-sidebar-panel space-y-2">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Risk Summary</p>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Resolution Rate</span>
          <span className={cn('text-[12px] font-semibold', resolutionRate >= 80 ? 'text-emerald-600' : 'text-amber-600')}>
            {resolutionRate}%
          </span>
        </div>
        <div className="h-[5px] w-full bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full', resolutionRate >= 80 ? 'bg-emerald-500' : 'bg-amber-500')}
            style={{ width: `${resolutionRate}%` }}
          />
        </div>
        {/* Severity breakdown */}
        <div className="grid grid-cols-2 gap-1.5 pt-1 text-[11px]">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-600" />Critical</span>
            <span className="font-medium text-red-600">{severityCounts.critical}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-400" />Severe</span>
            <span className="font-medium text-red-500">{severityCounts.severe}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />Moderate</span>
            <span className="font-medium text-amber-600">{severityCounts.moderate}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Mild</span>
            <span className="font-medium text-emerald-600">{severityCounts.mild}</span>
          </div>
        </div>
      </div>

      {/* Pending Escalations */}
      {pendingEscalations.length > 0 && (
        <div className="p360-alert-banner space-y-1.5" style={{ borderColor: colorSystem.warning.mid }}>
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-orange-600" />
            <p className="text-[11px] font-semibold text-foreground">Pending Escalations ({pendingEscalations.length})</p>
          </div>
          <div className="space-y-1">
            {pendingEscalations.slice(0, 3).map(e => (
              <div key={e.adverseEventId} className="flex items-center justify-between text-[11px]">
                <span className="text-foreground truncate font-mono">
                  {e.reportCode || e.adverseEventId.slice(0, 8).toUpperCase()}
                </span>
                <StatusBadge status={severityInfo(e.severity).label} variant={severityInfo(e.severity).variant} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SLA Deadlines */}
      {recentFollowUps.length > 0 && (
        <div className="p360-sidebar-panel space-y-1.5">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">SLA Deadlines</p>
          <div className="space-y-1">
            {recentFollowUps.map(e => {
              const deadline = new Date(e.slaDeadlineUtc!);
              const isOverdue = deadline.getTime() < Date.now();
              return (
                <div key={e.adverseEventId} className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground font-mono truncate">
                    {e.reportCode || e.adverseEventId.slice(0, 8).toUpperCase()}
                  </span>
                  <span className={cn('shrink-0', isOverdue ? 'text-destructive font-semibold' : 'text-muted-foreground')}>
                    {isOverdue ? 'OVERDUE' : formatDisplayDate(e.slaDeadlineUtc!)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Last Activity */}
      {events.length > 0 && (
        <div className="p360-sidebar-panel space-y-1">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Last Activity</p>
          <p className="text-[12px] font-medium text-foreground">
            {formatDisplayDate(events[0].createdOnUtc)}
          </p>
          <p className="text-[11px] text-muted-foreground capitalize">
            {events[0].reportType?.toLowerCase() || 'Adverse event'} · {severityInfo(events[0].severity).label}
          </p>
        </div>
      )}
    </div>
  );
}

/** Empty state — compact, no duplicate CTA (toolbar already has primary action) */
function AeEmptyState({ onQuickReport, workflowBlocked = false, onConfigureWorkflow }: {
  onQuickReport: (type: AeFormState['reportType'], severity: string) => void;
  workflowBlocked?: boolean;
  onConfigureWorkflow?: () => void;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/10 p-4 text-center">
      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/20">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
      </div>
      <h4 className="text-[13px] font-semibold text-foreground mt-2">No adverse events reported</h4>
      <p className="text-[11px] text-muted-foreground mt-0.5 max-w-md mx-auto">
        {workflowBlocked
          ? 'Adverse Event workflow is not configured for this program. Configure workflow before creating a report.'
          : 'Use “Report Event” above, or pick a quick template below to start a draft.'}
      </p>

      {workflowBlocked && onConfigureWorkflow && (
        <div className="mt-2 flex justify-center">
          <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={onConfigureWorkflow}>
            <AlertTriangle className="h-3 w-3" /> Configure Workflow
          </Button>
        </div>
      )}

      <div className="mt-3">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">Quick Templates</p>
        <div className="flex flex-wrap justify-center gap-1.5">
          {[
            { label: 'Side Effect', type: 'AE' as const, sev: 'MODERATE' },
            { label: 'Product Complaint', type: 'PC' as const, sev: 'MODERATE' },
            { label: 'Lack of Efficacy', type: 'AE' as const, sev: 'MODERATE' },
            { label: 'Serious AE', type: 'AE' as const, sev: 'SEVERE' },
          ].map(t => (
            <button
              key={t.label}
              type="button"
              disabled={workflowBlocked}
              onClick={() => onQuickReport(t.type, t.sev)}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-medium transition-colors',
                workflowBlocked
                  ? 'cursor-not-allowed text-muted-foreground opacity-60'
                  : 'text-foreground hover:bg-accent',
              )}
            >
              <Zap className="h-2.5 w-2.5 text-muted-foreground" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Report Drawer ───────────────────────────────────────────

function ReportDrawer({
  open,
  onClose,
  form,
  onChange,
  onSaveDraft,
  onSubmit,
  saving,
  submitting,
  patientName,
  patientCode,
  programId,
}: {
  open: boolean;
  onClose: () => void;
  form: AeFormState;
  onChange: (f: AeFormState) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  saving: boolean;
  submitting: boolean;
  patientName?: string;
  patientCode?: string;
  programId: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { showToast } = useToast();

  if (!open) return null;

  const set = <K extends keyof AeFormState>(k: K, v: AeFormState[K]) => onChange({ ...form, [k]: v });
  const busy = saving || submitting;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so same file can be re-selected
    e.target.value = '';

    // Validate file size (max 20 MB)
    if (file.size > 20 * 1024 * 1024) {
      showToast('File size exceeds 20 MB limit', 'error');
      return;
    }

    setUploading(true);
    try {
      const result: AeDocumentUploadResult = await adverseEventService.uploadDocument(programId, file);
      const newAttachment: AeAttachment = {
        s3Key: result.s3Key,
        signedUrl: result.signedUrl,
        fileName: result.fileName,
        contentType: result.contentType,
        fileSize: result.fileSize,
      };
      onChange({ ...form, attachments: [...form.attachments, newAttachment] });
      showToast(`${result.fileName} uploaded successfully`, 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'File upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index: number) => {
    onChange({ ...form, attachments: form.attachments.filter((_, i) => i !== index) });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return createPortal(
    <>
      {/* Full-screen saving overlay */}
      {busy && (
        <div className="app-right-drawer-saving-overlay">
          <AppInlineLoader className="text-white" label="Loading content" />
          <p className="text-white text-[12px] font-medium mt-4">{submitting ? 'Submitting report…' : 'Saving draft…'}</p>
        </div>
      )}

      {/* Backdrop — starts below app header */}
      <div className="app-right-drawer-backdrop" onClick={onClose} />

      {/* Drawer — starts below app header */}
      <div className="app-right-drawer animate-in slide-in-from-right duration-300">

        {/* Header */}
        <div className="app-right-drawer-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-foreground">Report Adverse Event</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">Capture and track safety events</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-md" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="app-right-drawer-body space-y-4">

          {/* Patient context bar */}
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/10 px-4 py-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <Shield className="h-3.5 w-3.5 text-amber-600" />
            </div>
            <div className="text-[12px] leading-tight min-w-0">
              <p className="font-semibold text-foreground truncate">{patientName || 'Patient'}</p>
              {patientCode && <p className="text-[11px] text-muted-foreground mt-0.5">{patientCode}</p>}
            </div>
          </div>

          {/* Event Classification — form card matching workspace AppCard */}
          <div className="rounded-lg border border-border bg-card">
            <div className="p-4 pb-3 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground">Event Classification</p>
            </div>
            <div className="p-4 pt-3 space-y-3">
              <div className="grid grid-cols-12 gap-3">
                {/* Report Type */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Report Type</label>
                  <select
                    value={form.reportType}
                    onChange={(e) => set('reportType', e.target.value as AeFormState['reportType'])}
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  >
                    <option value="AE">Adverse Event</option>
                    <option value="PC">Product Complaint</option>
                    <option value="BOTH">Both AE + PC</option>
                  </select>
                </div>

                {/* Severity */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Severity</label>
                  <select
                    value={form.severity}
                    onChange={(e) => set('severity', e.target.value)}
                    className={cn(
                      'w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none',
                      form.severity === 'MILD' && 'text-blue-700 dark:text-blue-400',
                      form.severity === 'MODERATE' && 'text-amber-700 dark:text-amber-400',
                      form.severity === 'SEVERE' && 'text-red-600 dark:text-red-400',
                      (form.severity === 'CRITICAL' || form.severity === 'LIFE_THREATENING') && 'text-purple-700 dark:text-purple-400',
                    )}
                  >
                    <option value="MILD">Mild</option>
                    <option value="MODERATE">Moderate</option>
                    <option value="SEVERE">Severe</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="LIFE_THREATENING">Life Threatening</option>
                  </select>
                </div>

                {/* Event Type (symptom) */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Symptom / Event Type</label>
                  <input
                    type="text"
                    value={form.eventType}
                    onChange={(e) => set('eventType', e.target.value)}
                    placeholder="e.g. Nausea, Rash"
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  />
                </div>

                {/* Onset Date */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Onset Date</label>
                  <input
                    type="date"
                    value={form.eventDate}
                    onChange={(e) => set('eventDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  />
                </div>

                {/* Suspected Product */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Suspected Product</label>
                  <input
                    type="text"
                    value={form.suspectedProduct}
                    onChange={(e) => set('suspectedProduct', e.target.value)}
                    placeholder="e.g. Trastuzumab 440mg"
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  />
                </div>

                {/* Causality */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Causality</label>
                  <select
                    value={form.causality}
                    onChange={(e) => set('causality', e.target.value as AeFormState['causality'])}
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  >
                    <option value="">— Select —</option>
                    <option value="YES">Yes (related)</option>
                    <option value="NO">No (not related)</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>

                {/* Outcome */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Outcome</label>
                  <select
                    value={form.outcome}
                    onChange={(e) => set('outcome', e.target.value)}
                    className="w-full h-9 rounded-md border border-border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  >
                    <option value="">— Select —</option>
                    <option value="RECOVERING">Recovering</option>
                    <option value="RECOVERED">Recovered</option>
                    <option value="NOT_RECOVERED">Not recovered</option>
                    <option value="FATAL">Fatal</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>

                {/* Description — full width */}
                <div className="col-span-12 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="Describe the adverse event, circumstances, and patient condition..."
                    className="w-full min-h-[120px] resize-none rounded-md border border-border bg-background px-3 py-2 text-[12px] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Safety Flags — form card matching workspace AppCard */}
          <div className="rounded-lg border border-border bg-card">
            <div className="p-4 pb-3 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground">Safety Flags</p>
            </div>
            <div className="p-4 pt-3 space-y-3">
              {/* Serious Event toggle */}
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-3">
                <div className="pr-3">
                  <p className="text-[12px] font-medium text-foreground">Serious Event</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Requires expedited reporting</p>
                </div>
                <Switch
                  checked={form.seriousEvent === true}
                  onCheckedChange={(checked) => set('seriousEvent', checked)}
                />
              </div>

              {/* Escalate to PV toggle */}
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-3">
                <div className="pr-3">
                  <p className="text-[12px] font-medium text-foreground">Escalate to PV Team</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Immediately flag for pharmacovigilance review</p>
                </div>
                <Switch
                  checked={form.escalateToPv === true}
                  onCheckedChange={(checked) => set('escalateToPv', checked)}
                />
              </div>
            </div>
          </div>

          {/* File Attachment card */}
          <div className="rounded-lg border border-border bg-card">
            <div className="p-4 pb-3 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                Supporting Documents
              </p>
            </div>
            <div className="p-4 pt-3 space-y-2">
              {/* Uploaded files list */}
              {form.attachments.length > 0 && (
                <div className="space-y-1.5">
                  {form.attachments.map((att, idx) => (
                    <div
                      key={att.s3Key}
                      className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2"
                    >
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-foreground truncate">{att.fileName}</p>
                        <p className="text-[11px] text-muted-foreground">{formatFileSize(att.fileSize)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-destructive/10 transition-colors shrink-0"
                        title="Remove"
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleFileSelect}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || busy}
                className={cn(
                  'w-full rounded-lg border border-dashed border-border bg-muted/10 p-4 text-center transition-colors',
                  'hover:bg-accent/50 hover:border-primary/40 cursor-pointer',
                  (uploading || busy) && 'opacity-50 cursor-not-allowed',
                )}
              >
                {uploading ? (
                  <>
                    <AppInlineLoader className="justify-center mb-1.5" label="Loading content" />
                    <p className="text-[11px] font-medium text-foreground">Uploading</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mx-auto text-muted-foreground mb-1.5" />
                    <p className="text-[11px] font-medium text-foreground">Click to upload</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">PDF, images, documents (max 20 MB)</p>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="app-right-drawer-footer">
          <Button variant="outline" size="sm" className="h-9 px-4 text-[12px]" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="outline" size="sm" className="h-9 px-4 text-[12px] gap-1" onClick={onSaveDraft} disabled={!form.description || busy}>
            <Save className="h-3 w-3" />
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button size="sm" className="h-9 px-4 text-[12px] gap-1" onClick={onSubmit} disabled={!form.description || busy}>
            <Send className="h-3 w-3" />
            {submitting ? 'Submitting…' : 'Submit Report'}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function DrawerField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

// ─── Detail Modal (preserved for View Details) ───────────────

function DetailModal({
  adverseEventId,
  detail,
  loading,
  timeline,
  escalationHistory,
  notificationHistory,
  userIsPv,
  onClose,
  onSubmit,
  onSaveDraft,
  onAddLcn,
  submitPending,
}: {
  adverseEventId: string;
  detail: AdverseEventDetailDto | null;
  loading: boolean;
  timeline: AdverseEventTimelineEntryDto[];
  escalationHistory: AeEscalationHistoryDto[];
  notificationHistory: Communication[];
  userIsPv: boolean;
  onClose: () => void;
  onSubmit: (form: AeFormState) => void;
  onSaveDraft: (form: AeFormState) => Promise<void>;
  onAddLcn: () => void;
  submitPending: boolean;
}) {
  const initial: AeFormState = useMemo(() => {
    if (!detail) return EMPTY_FORM;
    let payload: Partial<Record<string, unknown>> = {};
    if (detail.formDataJson) {
      try { payload = JSON.parse(detail.formDataJson) as Record<string, unknown>; } catch { /* keep empty */ }
    }
    const attachments: AeAttachment[] = Array.isArray(payload.attachments)
      ? (payload.attachments as Array<Record<string, unknown>>).map(a => ({
          s3Key: (a.s3_key as string) || '',
          signedUrl: '',
          fileName: (a.file_name as string) || '',
          contentType: (a.content_type as string) || '',
          fileSize: (a.file_size as number) || 0,
        }))
      : [];
    return {
      reportType: (detail.reportType || payload.report_type || 'AE') as AeFormState['reportType'],
      severity: detail.severity || 'MODERATE',
      eventDate: detail.eventDate || '',
      description: detail.description || '',
      suspectedProduct: (payload.suspected_product as string) || '',
      eventType: (payload.event_type as string) || '',
      causality: ((payload.causality as string) || '') as AeFormState['causality'],
      seriousEvent: !!payload.serious_event,
      outcome: (payload.outcome as string) || '',
      escalateToPv: !!payload.escalate_to_pv,
      attachments,
    };
  }, [detail]);

  const [form, setForm] = useState<AeFormState>(initial);
  useEffect(() => { setForm(initial); }, [initial]);

  if (!detail && !loading) return null;
  const phase: LifecyclePhase = detail ? normalizePhase(detail.lifecyclePhase || detail.status) : 'DRAFT';
  const ph = phaseBadge(phase);
  const isClosed = phase === 'CLOSED';
  const canEdit = phase === 'DRAFT';
  const canSubmit = phase === 'DRAFT';
  const canAddLcn = userIsPv && (phase === 'SUBMITTED' || phase === 'PV_REVIEW');

  return (
    <AppModal open={!!adverseEventId} onClose={onClose} size="xl">
      <AppModalHeader>
        <div className="flex items-center gap-2">
          <AppModalTitle>
            {detail?.reportCode || `AE ${adverseEventId.slice(0, 8).toUpperCase()}`}
          </AppModalTitle>
          <StatusBadge status={ph.label} variant={ph.variant} />
        </div>
        <AppModalDescription>
          Lifecycle: Draft → Submitted → PV Review → Closed
        </AppModalDescription>
      </AppModalHeader>
      <AppModalBody>
        {loading || !detail ? (
          <AppInlineLoader label="Loading content" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <fieldset disabled={!canEdit} className={!canEdit ? 'opacity-90 pointer-events-none' : ''}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DrawerField label="Report Type">
                    <select className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.reportType} onChange={(e) => setForm({ ...form, reportType: e.target.value as AeFormState['reportType'] })}>
                      <option value="AE">Adverse Event</option>
                      <option value="PC">Product Complaint</option>
                      <option value="BOTH">Both AE + PC</option>
                    </select>
                  </DrawerField>
                  <DrawerField label="Severity">
                    <select className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                      <option value="MILD">Mild</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="SEVERE">Severe</option>
                      <option value="CRITICAL">Critical</option>
                      <option value="LIFE_THREATENING">Life Threatening</option>
                    </select>
                  </DrawerField>
                  <DrawerField label="Event Date">
                    <input type="date" className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} />
                  </DrawerField>
                  <DrawerField label="Causality">
                    <select className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.causality} onChange={(e) => setForm({ ...form, causality: e.target.value as AeFormState['causality'] })}>
                      <option value="">—</option>
                      <option value="YES">Yes</option>
                      <option value="NO">No</option>
                      <option value="UNKNOWN">Unknown</option>
                    </select>
                  </DrawerField>
                  <DrawerField label="Suspected Product">
                    <input type="text" className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.suspectedProduct} onChange={(e) => setForm({ ...form, suspectedProduct: e.target.value })} placeholder="e.g. Trastuzumab" />
                  </DrawerField>
                  <DrawerField label="Event Type">
                    <input type="text" className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })} placeholder="e.g. Nausea, Rash" />
                  </DrawerField>
                  <div className="md:col-span-2">
                    <DrawerField label="Description">
                      <textarea className="w-full h-20 resize-none rounded-md border border-border bg-background px-3 py-2 text-[12px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the event…" />
                    </DrawerField>
                  </div>
                </div>
              </fieldset>
              {detail.lcnNumber && (
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Hash className="w-3 h-3" /> Local Case Number
                  </p>
                  <p className="text-[13px] font-semibold font-mono">{detail.lcnNumber}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Source: {detail.lcnSource || '—'}
                    {detail.lcnAssignedAtUtc ? ` · ${formatDisplayDate(detail.lcnAssignedAtUtc)}` : ''}
                  </p>
                </div>
              )}
            </div>

            <aside className="space-y-3">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Timeline
              </p>
              <ol className="space-y-2 border-l-2 border-border pl-3">
                {timeline.length === 0 && (
                  <li className="text-[11px] text-muted-foreground">No activity yet.</li>
                )}
                {timeline.map((t, i) => (
                  <li key={`${t.timestampUtc}-${i}`} className="relative">
                    <span className="absolute -left-[13px] top-1.5 w-2 h-2 rounded-full bg-primary" />
                    <p className="text-[11px] font-medium text-foreground">{t.phase.replace(/_/g, ' ')}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {formatDisplayDate(t.timestampUtc)} · {t.actorRole || 'system'}
                    </p>
                    {t.note && <p className="text-[11px] text-muted-foreground/80">{t.note}</p>}
                  </li>
                ))}
              </ol>

              <div className="rounded-md border border-border bg-muted/10 p-3">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Escalation History</p>
                <div className="mt-2 space-y-2">
                  {escalationHistory.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">No escalation activity recorded.</p>
                  ) : (
                    escalationHistory.slice(0, 4).map((item) => (
                      <div key={item.aeEscalationHistoryId} className="rounded border border-border bg-background px-2.5 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium text-foreground">{item.ruleName}</span>
                          <StatusBadge status={item.status} variant={item.status === 'TRIGGERED' ? 'warning' : 'success'} />
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{item.triggerReason}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">{formatDisplayDate(item.createdOn)}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-muted/10 p-3">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Notification History</p>
                <div className="mt-2 space-y-2">
                  {notificationHistory.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">No notifications logged for this report.</p>
                  ) : (
                    notificationHistory.slice(0, 4).map((item) => (
                      <div key={item.id} className="rounded border border-border bg-background px-2.5 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium text-foreground truncate">{item.recipientName || item.recipient || item.eventCode}</span>
                          <StatusBadge status={item.status} variant={item.status === 'Failed' ? 'error' : 'success'} />
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">{item.channel} · {formatDisplayDate(item.createdAt)}</p>
                        {item.failureReason && <p className="mt-1 text-[11px] text-destructive line-clamp-2">{item.failureReason}</p>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </aside>
          </div>
        )}
      </AppModalBody>
      <AppModalFooter>
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        {canEdit && (
          <Button size="sm" variant="outline" className="gap-1" onClick={() => onSaveDraft(form)}>
            <Save className="w-3.5 h-3.5" /> Save Draft
          </Button>
        )}
        {canSubmit && (
          <Button size="sm" className="gap-1" disabled={!form.description || submitPending} onClick={() => onSubmit(form)}>
            <Send className="w-3.5 h-3.5" /> {submitPending ? 'Submitting…' : 'Submit to PV'}
          </Button>
        )}
        {canAddLcn && (
          <Button size="sm" className="gap-1" onClick={onAddLcn}>
            <ShieldCheck className="w-3.5 h-3.5" /> Add LCN & Close
          </Button>
        )}
        {isClosed && (
          <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Report closed
          </span>
        )}
      </AppModalFooter>
    </AppModal>
  );
}

// ─── LCN Modal ───────────────────────────────────────────────

function LcnModal({
  open,
  onClose,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (lcn: string, source: 'MANUAL' | 'EXTERNAL', comments?: string) => void;
}) {
  const [lcn, setLcn] = useState('');
  const [source, setSource] = useState<'MANUAL' | 'EXTERNAL'>('MANUAL');
  const [comments, setComments] = useState('');
  return (
    <AppModal open={open} onClose={onClose} size="default">
      <AppModalHeader>
        <AppModalTitle>Add Local Case Number</AppModalTitle>
        <AppModalDescription>
          Adding an LCN closes the report and records the PV decision.
        </AppModalDescription>
      </AppModalHeader>
      <AppModalBody>
        <div className="space-y-3">
          <DrawerField label="LCN Number">
            <input
              type="text"
              className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px] font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={lcn}
              onChange={(e) => setLcn(e.target.value)}
              placeholder="e.g. LCN-2026-001234"
              autoFocus
            />
          </DrawerField>
          <DrawerField label="Source">
            <select
              className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={source}
              onChange={(e) => setSource(e.target.value as 'MANUAL' | 'EXTERNAL')}
            >
              <option value="MANUAL">Manual</option>
              <option value="EXTERNAL">External (PV system)</option>
            </select>
          </DrawerField>
          <DrawerField label="Comments (optional)">
            <textarea
              className="w-full h-16 resize-none rounded-md border border-border bg-background px-3 py-2 text-[12px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </DrawerField>
        </div>
      </AppModalBody>
      <AppModalFooter>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button
          size="sm"
          className="gap-1"
          disabled={!lcn.trim() || submitting}
          onClick={() => onSubmit(lcn.trim(), source, comments || undefined)}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {submitting ? 'Saving…' : 'Add LCN & Close'}
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}

function FollowUpModal({
  open,
  onClose,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (payload: AdverseEventFollowUpRequest) => void;
}) {
  const [title, setTitle] = useState('PV follow-up required');
  const [description, setDescription] = useState('Review this report, capture follow-up findings, and update the case timeline.');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('HIGH');
  const [assignedToRole, setAssignedToRole] = useState('PV');
  const [dueAtUtc, setDueAtUtc] = useState(() => new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().slice(0, 16));

  return (
    <AppModal open={open} onClose={onClose} size="default">
      <AppModalHeader>
        <AppModalTitle>Create Follow-up Task</AppModalTitle>
        <AppModalDescription>Add a tracked follow-up task linked to this AE/PC report.</AppModalDescription>
      </AppModalHeader>
      <AppModalBody>
        <div className="space-y-3">
          <DrawerField label="Title">
            <input className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={title} onChange={(e) => setTitle(e.target.value)} />
          </DrawerField>
          <DrawerField label="Description">
            <textarea className="w-full h-20 resize-none rounded-md border border-border bg-background px-3 py-2 text-[12px]" value={description} onChange={(e) => setDescription(e.target.value)} />
          </DrawerField>
          <div className="grid grid-cols-2 gap-3">
            <DrawerField label="Priority">
              <select className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={priority} onChange={(e) => setPriority(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL')}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </DrawerField>
            <DrawerField label="Assign To Role">
              <input className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={assignedToRole} onChange={(e) => setAssignedToRole(e.target.value)} placeholder="PV" />
            </DrawerField>
          </div>
          <DrawerField label="Due Date & Time">
            <input type="datetime-local" className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={dueAtUtc} onChange={(e) => setDueAtUtc(e.target.value)} />
          </DrawerField>
        </div>
      </AppModalBody>
      <AppModalFooter>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button
          size="sm"
          className="gap-1"
          disabled={!title.trim() || submitting}
          onClick={() => onSubmit({
            title: title.trim(),
            description: description.trim() || null,
            priority,
            assignedToRole: assignedToRole.trim() || null,
            dueAtUtc: dueAtUtc ? new Date(dueAtUtc).toISOString() : null,
          })}
        >
          <Plus className="w-3.5 h-3.5" />
          {submitting ? 'Creating…' : 'Create Follow-up'}
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}

function EscalationModal({
  open,
  onClose,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (payload: AdverseEventManualEscalationRequest) => void;
}) {
  const [reason, setReason] = useState('Manual escalation requested from Patient360 action menu.');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('HIGH');
  const [assignedToRole, setAssignedToRole] = useState('PV');
  const [notifyPvTeam, setNotifyPvTeam] = useState(true);

  return (
    <AppModal open={open} onClose={onClose} size="default">
      <AppModalHeader>
        <AppModalTitle>Escalate Report</AppModalTitle>
        <AppModalDescription>Create an escalation record and optionally notify the PV team.</AppModalDescription>
      </AppModalHeader>
      <AppModalBody>
        <div className="space-y-3">
          <DrawerField label="Reason">
            <textarea className="w-full h-24 resize-none rounded-md border border-border bg-background px-3 py-2 text-[12px]" value={reason} onChange={(e) => setReason(e.target.value)} />
          </DrawerField>
          <div className="grid grid-cols-2 gap-3">
            <DrawerField label="Priority">
              <select className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={priority} onChange={(e) => setPriority(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL')}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </DrawerField>
            <DrawerField label="Assign To Role">
              <input className="w-full h-8 rounded-md border border-border bg-background px-3 text-[12px]" value={assignedToRole} onChange={(e) => setAssignedToRole(e.target.value)} placeholder="PV" />
            </DrawerField>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-3">
            <div>
              <p className="text-[12px] font-medium text-foreground">Notify PV Team</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Trigger configured escalation notifications after saving.</p>
            </div>
            <Switch checked={notifyPvTeam} onCheckedChange={setNotifyPvTeam} />
          </div>
        </div>
      </AppModalBody>
      <AppModalFooter>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button
          size="sm"
          className="gap-1"
          disabled={!reason.trim() || submitting}
          onClick={() => onSubmit({
            reason: reason.trim(),
            priority,
            assignedToRole: assignedToRole.trim() || null,
            notifyPvTeam,
          })}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          {submitting ? 'Escalating…' : 'Escalate'}
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}

// ─── Main Component ──────────────────────────────────────────

export function AdverseEventsTab({
  programId,
  patientId,
  adverseEvents,
  currentCycleId = null,
  onRefresh,
  patientName,
  patientCode,
}: AdverseEventsTabProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userIsPv = isPvRole(user?.programRoleName);

  // ── Live list (server-truth) ─────────────────────────────
  const listKey = ['ae-pc', 'list', programId, patientId];
  const { data: liveList, isFetching, refetch } = useQuery({
    queryKey: listKey,
    queryFn: () => adverseEventService.listByPatient(programId, patientId),
    enabled: !!programId && !!patientId,
  });
  const { data: workflowStatus } = useQuery({
    queryKey: ['ae-pc', 'workflow-status', programId],
    queryFn: () => adverseEventService.getWorkflowStatus(programId),
    enabled: !!programId,
  });

  const events = useMemo(() => {
    if (liveList && liveList.length > 0) {
      return liveList.map<Patient360AdverseEventDto>((e) => ({
        adverseEventId: e.adverseEventId,
        caseId: null,
        cycleId: e.treatmentCycleId,
        cycleNumber: null,
        severity: e.severity,
        status: e.status,
        eventDate: e.eventDate,
        description: e.description,
        workflowState: e.workflowState,
        currentWorkflowStepId: e.currentWorkflowStepId,
        currentWorkflowStepName: e.currentWorkflowStepName,
        stepEnteredAtUtc: e.stepEnteredAtUtc,
        createdOnUtc: e.createdOnUtc,
        slaDeadlineUtc: e.slaDeadlineUtc,
        isSlaBreached: e.isSlaBreached,
      }));
    }
    return adverseEvents;
  }, [liveList, adverseEvents]);

  const sorted = useMemo(
    () => [...events].sort((a, b) => new Date(b.createdOnUtc).getTime() - new Date(a.createdOnUtc).getTime()),
    [events],
  );

  // ── Filter + Search ──────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const summary = useMemo(() => {
    const total = events.length;
    const open = events.filter(e => normalizePhase(e.status) !== 'CLOSED').length;
    const closed = events.filter(e => normalizePhase(e.status) === 'CLOSED').length;
    const serious = events.filter(e => isSeriousEvent(e.severity)).length;
    const escalated = events.filter(e => normalizePhase(e.status) === 'PV_REVIEW').length;
    return { total, open, closed, serious, escalated };
  }, [events]);

  const filtered = useMemo(() => {
    let result = sorted;
    if (statusFilter === 'open') result = result.filter(e => normalizePhase(e.status) !== 'CLOSED');
    else if (statusFilter === 'closed') result = result.filter(e => normalizePhase(e.status) === 'CLOSED');
    else if (statusFilter === 'serious') result = result.filter(e => isSeriousEvent(e.severity));
    else if (statusFilter === 'escalated') result = result.filter(e => normalizePhase(e.status) === 'PV_REVIEW');

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        (e.description?.toLowerCase().includes(q)) ||
        (e.reportCode?.toLowerCase().includes(q)) ||
        (e.reportType?.toLowerCase().includes(q)) ||
        (e.adverseEventId.toLowerCase().includes(q))
      );
    }
    return result;
  }, [sorted, statusFilter, search]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const workflowBlocked = !!workflowStatus && !workflowStatus.isConfigured;
  const workflowBlockMessage = workflowStatus?.missingRequirements?.[0]
    ? `Adverse Event workflow is not configured: ${workflowStatus.missingRequirements[0]}.`
    : 'Adverse Event workflow is not configured for this program.';

  // ── Drawer state ─────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerForm, setDrawerForm] = useState<AeFormState>(EMPTY_FORM);

  // ── Detail modal state ───────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const [lcnOpen, setLcnOpen] = useState(false);
  const [followUpEventId, setFollowUpEventId] = useState<string | null>(null);
  const [escalationEventId, setEscalationEventId] = useState<string | null>(null);

  const { data: detail, isFetching: detailLoading } = useQuery({
    queryKey: ['ae-pc', 'detail', detailId],
    queryFn: () => adverseEventService.getById(programId, detailId!),
    enabled: !!detailId,
  });
  const { data: timeline = [] } = useQuery({
    queryKey: ['ae-pc', 'timeline', detailId],
    queryFn: () => adverseEventService.getTimeline(programId, detailId!),
    enabled: !!detailId,
  });
  const { data: escalationHistory = { items: [] } } = useQuery({
    queryKey: ['ae-pc', 'escalation-history', programId, detailId],
    queryFn: () => aeEscalationService.listHistory(programId, { adverseEventId: detailId!, pageSize: 10 }),
    enabled: !!detailId,
  });
  const { data: notificationHistory = { items: [] } } = useQuery({
    queryKey: ['ae-pc', 'communications', programId, detailId],
    queryFn: () => communicationService.listForProgram(programId, {
      triggeredFromEntityType: 'AdverseEvent',
      triggeredFromEntityId: detailId!,
      pageSize: 10,
    }),
    enabled: !!detailId,
  });

  const refreshAll = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ae-pc'] }),
      refetch(),
    ]);
    onRefresh?.();
  }, [queryClient, refetch, onRefresh]);

  // ── Mutations ────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (form: AeFormState) => {
      const created = await adverseEventService.create(programId, patientId, {
        treatmentCycleId: currentCycleId,
        severity: form.severity,
        eventDate: form.eventDate || null,
        description: form.description,
      });
      await adverseEventService.saveDraft(programId, created.adverseEventId, {
        reportType: form.reportType,
        severity: form.severity,
        eventDate: form.eventDate || null,
        description: form.description,
        treatmentCycleId: currentCycleId,
        formDataJson: buildFormPayload(form),
      });
      return created;
    },
    onSuccess: async () => {
      showToast('Adverse event draft created', 'success');
      setDrawerOpen(false);
      setDrawerForm(EMPTY_FORM);
      await refreshAll();
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to create report', 'error');
    },
  });

  const submitMutation = useMutation({
    mutationFn: async ({ id, form }: { id?: string; form: AeFormState }) => {
      let aeId = id;
      if (!aeId) {
        // Create then immediately submit
        const created = await adverseEventService.create(programId, patientId, {
          treatmentCycleId: currentCycleId,
          severity: form.severity,
          eventDate: form.eventDate || null,
          description: form.description,
        });
        aeId = created.adverseEventId;
      }
      return adverseEventService.submit(programId, aeId, {
        reportType: form.reportType,
        severity: form.severity,
        eventDate: form.eventDate || null,
        description: form.description,
        treatmentCycleId: currentCycleId,
        formDataJson: buildFormPayload(form),
      });
    },
    onSuccess: async () => {
      showToast('Report submitted to PV team', 'success');
      setDrawerOpen(false);
      setDrawerForm(EMPTY_FORM);
      await refreshAll();
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Submit failed', 'error');
    },
  });

  const lcnMutation = useMutation({
    mutationFn: async ({ id, lcn, source, comments }: { id: string; lcn: string; source: 'MANUAL' | 'EXTERNAL'; comments?: string }) =>
      adverseEventService.addLcn(programId, id, { lcnNumber: lcn, lcnSource: source, comments: comments || null }),
    onSuccess: async () => {
      showToast('LCN added — report closed', 'success');
      setLcnOpen(false);
      await refreshAll();
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to add LCN', 'error');
    },
  });

  const followUpMutation = useMutation({
    mutationFn: async ({ adverseEventId, payload }: { adverseEventId: string; payload: AdverseEventFollowUpRequest }) =>
      adverseEventService.createFollowUp(programId, adverseEventId, payload),
    onSuccess: async () => {
      showToast('Follow-up task created', 'success');
      setFollowUpEventId(null);
      await queryClient.invalidateQueries({ queryKey: ['ae-pc'] });
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to create follow-up', 'error');
    },
  });

  const manualEscalationMutation = useMutation({
    mutationFn: async ({ adverseEventId, payload }: { adverseEventId: string; payload: AdverseEventManualEscalationRequest }) =>
      adverseEventService.manualEscalate(programId, adverseEventId, payload),
    onSuccess: async () => {
      showToast('Escalation recorded', 'success');
      setEscalationEventId(null);
      await refreshAll();
      if (detailId) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['ae-pc', 'detail', detailId] }),
          queryClient.invalidateQueries({ queryKey: ['ae-pc', 'escalation-history', programId, detailId] }),
          queryClient.invalidateQueries({ queryKey: ['ae-pc', 'communications', programId, detailId] }),
        ]);
      }
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Escalation failed', 'error');
    },
  });

  // ── Handlers ─────────────────────────────────────────────
  const openDrawer = useCallback((presetType?: AeFormState['reportType'], presetSeverity?: string) => {
    if (workflowBlocked) {
      showToast(workflowBlockMessage, 'warning');
      return;
    }
    setDrawerForm({
      ...EMPTY_FORM,
      reportType: presetType || 'AE',
      severity: presetSeverity || 'MODERATE',
      seriousEvent: presetSeverity === 'SEVERE' || presetSeverity === 'CRITICAL',
    });
    setDrawerOpen(true);
  }, [showToast, workflowBlocked, workflowBlockMessage]);

  const handleConfigureWorkflow = useCallback(() => {
    navigate(programRoutes.workflow(programId));
  }, [navigate, programId]);

  const handleDrawerSaveDraft = useCallback(() => {
    createMutation.mutate(drawerForm);
  }, [createMutation, drawerForm]);

  const handleDrawerSubmit = useCallback(() => {
    submitMutation.mutate({ form: drawerForm });
  }, [submitMutation, drawerForm]);

  const triggerConfiguredNotification = useCallback(async (event: Patient360AdverseEventDto, eventType: string, successMessage: string) => {
    try {
      await notificationConfigurationService.trigger.trigger({
        eventType,
        placeholders: {
          AdverseEventId: event.adverseEventId,
          ProgramId: programId,
          PatientId: patientId,
          ReportCode: event.reportCode || event.adverseEventId.slice(0, 8).toUpperCase(),
          ReportType: event.reportType || 'AE',
          Severity: event.severity || '',
          Status: event.status || '',
          TriggeredFrom: 'PATIENT360',
          TriggeredFromEntityType: 'AdverseEvent',
          TriggeredFromEntityId: event.adverseEventId,
          TriggeredByUserId: user?.userId || '',
          TriggeredByRole: user?.programRoleName || '',
        },
      });
      showToast(successMessage, 'success');
      if (detailId === event.adverseEventId) {
        await queryClient.invalidateQueries({ queryKey: ['ae-pc', 'communications', programId, detailId] });
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Notification send failed', 'error');
    }
  }, [detailId, patientId, programId, queryClient, showToast, user?.programRoleName, user?.userId]);

  const handleDownloadPdf = useCallback(async (event: Patient360AdverseEventDto) => {
    try {
      const result = await aeRegulatoryExportService.generateAndDownload(programId, {
        exportType: 'CASE',
        format: 'PDF',
        adverseEventId: event.adverseEventId,
        includeAuditTrail: true,
      });
      showToast(`Downloaded ${result.fileName}`, 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Download failed', 'error');
    }
  }, [programId, showToast]);

  const handleResendEscalationEmail = useCallback(async (event: Patient360AdverseEventDto) => {
    try {
      const history = await communicationService.listForProgram(programId, {
        triggeredFromEntityType: 'AdverseEvent',
        triggeredFromEntityId: event.adverseEventId,
        pageSize: 10,
      });
      const candidate = history.items.find((item) => item.canRetrigger) || history.items[0];
      if (!candidate) {
        showToast('No prior communication found for this report', 'warning');
        return;
      }

      const response = await communicationService.retrigger(candidate.id, {
        triggeredFrom: 'PATIENT360',
        note: 'Resent from AE/PC action menu',
      });
      if (response.status.toUpperCase() === 'FAILED') {
        showToast(response.failureReason || 'Retrigger failed', 'error');
        return;
      }
      showToast('Escalation email resent', 'success');
      if (detailId === event.adverseEventId) {
        await queryClient.invalidateQueries({ queryKey: ['ae-pc', 'communications', programId, detailId] });
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Resend failed', 'error');
    }
  }, [detailId, programId, queryClient, showToast]);

  const hasEvents = sorted.length > 0;

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {workflowBlocked && workflowStatus && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/60 dark:bg-amber-950/20">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-amber-900 dark:text-amber-200">
                Adverse Event workflow setup required
              </p>
              <p className="text-[11px] text-amber-800 dark:text-amber-300">
                {workflowStatus.missingRequirements.join(', ')}. AE creation is disabled until workflow setup is completed.
              </p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1 self-start md:self-auto" onClick={handleConfigureWorkflow}>
              <AlertTriangle className="h-3 w-3" /> Configure Workflow
            </Button>
          </div>
        </div>
      )}

      {/* Smart Alerts */}
      <SmartAlerts events={events} />

      {/* Filter bar + Quick Reports + Search + Actions */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Filter chips */}
        {([
          { key: 'all' as StatusFilter, label: 'All', count: summary.total },
          { key: 'open' as StatusFilter, label: 'Open', count: summary.open },
          { key: 'serious' as StatusFilter, label: 'Serious', count: summary.serious },
          { key: 'escalated' as StatusFilter, label: 'Escalated', count: summary.escalated },
          { key: 'closed' as StatusFilter, label: 'Closed', count: summary.closed },
        ]).map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
              statusFilter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/60 text-muted-foreground hover:bg-accent',
            )}
          >
            {f.label}
            {f.count > 0 && <span className="opacity-70">({f.count})</span>}
          </button>
        ))}

        {/* Right side: Search + Actions */}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports..."
              className="h-7 w-[140px] rounded-md border border-border bg-background pl-7 pr-2 text-[11px] placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => refreshAll()} disabled={isFetching}>
            {isFetching ? <AppButtonLoader compact /> : <RefreshCw className="h-3 w-3" />}
          </Button>
          <Button
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={() => openDrawer()}
            disabled={workflowBlocked}
            title={workflowBlocked ? workflowBlockMessage : undefined}
          >
            <Plus className="h-3 w-3" /> Report Event
          </Button>
        </div>
      </div>

      {/* Quick Report Templates */}
      {hasEvents && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mr-1">Quick:</span>
          {[
            { label: 'Side Effect', type: 'AE' as const, sev: 'MODERATE' },
            { label: 'Product Complaint', type: 'PC' as const, sev: 'MODERATE' },
            { label: 'Lack of Efficacy', type: 'AE' as const, sev: 'MODERATE' },
            { label: 'Medication Error', type: 'AE' as const, sev: 'SEVERE' },
            { label: 'Serious AE', type: 'AE' as const, sev: 'CRITICAL' },
          ].map(t => (
            <button
              key={t.label}
              type="button"
              disabled={workflowBlocked}
              onClick={() => openDrawer(t.type, t.sev)}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium transition-colors',
                workflowBlocked
                  ? 'cursor-not-allowed text-muted-foreground opacity-60'
                  : 'text-foreground hover:bg-accent',
              )}
              title={workflowBlocked ? workflowBlockMessage : undefined}
            >
              <Zap className="h-2.5 w-2.5 text-muted-foreground" />
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      {!hasEvents ? (
        <AeEmptyState
          workflowBlocked={workflowBlocked}
          onConfigureWorkflow={handleConfigureWorkflow}
          onQuickReport={(type, sev) => openDrawer(type, sev)}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
          {/* LEFT — AE Timeline */}
          <div className="space-y-4 min-w-0">
            {filtered.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-muted/10 p-4 text-center">
                <p className="text-[11px] text-muted-foreground">No reports match this filter.</p>
              </div>
            ) : (
              Array.from(grouped.entries()).map(([dateLabel, dateCalls]) => (
                <div key={dateLabel}>
                  {/* Date header */}
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{dateLabel}</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Cards for this date */}
                  <div className="space-y-2 ml-1 border-l-2 border-border pl-3">
                    {dateCalls.map(event => (
                      <AeTimelineCard
                        key={event.adverseEventId}
                        event={event}
                        onView={() => setDetailId(event.adverseEventId)}
                        onEdit={() => setDetailId(event.adverseEventId)}
                        onAddFollowUp={() => setFollowUpEventId(event.adverseEventId)}
                        onEscalate={() => setEscalationEventId(event.adverseEventId)}
                        onDownloadPdf={() => void handleDownloadPdf(event)}
                        onNotifyPv={() => void triggerConfiguredNotification(event, 'AE_PC_REPORT_ESCALATED', 'PV team notified')}
                        onResendEscalationEmail={() => void handleResendEscalationEmail(event)}
                        onSendNotification={() => void triggerConfiguredNotification(event, 'AE_PC_REPORT_SUBMITTED', 'Notification sent')}
                        onViewAuditTrail={() => setDetailId(event.adverseEventId)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* RIGHT — Safety Insights */}
          <SafetyInsightsPanel events={sorted} />
        </div>
      )}

      {/* Report Drawer */}
      <ReportDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        form={drawerForm}
        onChange={setDrawerForm}
        onSaveDraft={handleDrawerSaveDraft}
        onSubmit={handleDrawerSubmit}
        saving={createMutation.isPending}
        submitting={submitMutation.isPending}
        patientName={patientName}
        patientCode={patientCode}
        programId={programId}
      />

      {/* Detail Modal */}
      {detailId && (
        <DetailModal
          adverseEventId={detailId}
          detail={detail || null}
          loading={detailLoading}
          timeline={timeline}
          escalationHistory={escalationHistory.items}
          notificationHistory={notificationHistory.items}
          userIsPv={userIsPv}
          onClose={() => setDetailId(null)}
          onSubmit={(form) => submitMutation.mutate({ id: detailId, form })}
          submitPending={submitMutation.isPending}
          onSaveDraft={async (form) => {
            try {
              await adverseEventService.saveDraft(programId, detailId, {
                reportType: form.reportType,
                severity: form.severity,
                eventDate: form.eventDate || null,
                description: form.description,
                treatmentCycleId: currentCycleId,
                formDataJson: buildFormPayload(form),
              });
              showToast('Draft saved', 'success');
              await refreshAll();
            } catch (err) {
              showToast(err instanceof Error ? err.message : 'Save failed', 'error');
            }
          }}
          onAddLcn={() => setLcnOpen(true)}
        />
      )}

      {followUpEventId && (
        <FollowUpModal
          open={!!followUpEventId}
          onClose={() => setFollowUpEventId(null)}
          submitting={followUpMutation.isPending}
          onSubmit={(payload) => followUpMutation.mutate({ adverseEventId: followUpEventId, payload })}
        />
      )}

      {escalationEventId && (
        <EscalationModal
          open={!!escalationEventId}
          onClose={() => setEscalationEventId(null)}
          submitting={manualEscalationMutation.isPending}
          onSubmit={(payload) => manualEscalationMutation.mutate({ adverseEventId: escalationEventId, payload })}
        />
      )}

      {/* LCN Modal */}
      {detailId && lcnOpen && (
        <LcnModal
          open={lcnOpen}
          onClose={() => setLcnOpen(false)}
          submitting={lcnMutation.isPending}
          onSubmit={(lcn, source, comments) =>
            lcnMutation.mutate({ id: detailId, lcn, source, comments })
          }
        />
      )}
    </div>
  );
}

